import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface Profile {
  full_name: string;
  email: string;
  phone: string;
  avatar_url: string;
  tier: string;
  specialization: string;
}

export function useProfileData() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [profile, setProfile] = useState<Profile>({
    full_name: '',
    email: '',
    phone: '',
    avatar_url: '/placeholder.svg',
    tier: 'Beta',
    specialization: ''
  });

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          const { data: profileData, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', user.id)
            .single();

          if (error) {
            toast({
              variant: "destructive",
              title: "Error loading profile",
              description: error.message,
            });
            return;
          }

          if (profileData) {
            const profileWithDefaults = {
              full_name: profileData.full_name || '',
              email: profileData.email || '',
              phone: profileData.phone || '',
              avatar_url: profileData.avatar_url || '/placeholder.svg',
              tier: profileData.tier || 'Beta',
              specialization: profileData.specialization || ''
            };
            setProfile(profileWithDefaults);
          }
        }
      } catch (error: any) {
        toast({
          variant: "destructive",
          title: "Error loading profile",
          description: error.message,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [toast]);

  return {
    profile,
    setProfile,
    isLoading,
    setIsLoading
  };
}
